// src/stores/appointment.ts
import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  Adviser,
  Vehicle,
  Hour,
  AppointmentDate,
  AvailabilityHoursByDateMap,
  Appointment,
  QueriedVehicleSlugs,
  AppointmentItem
} from '~concierge/types/AppointmentTypes'
import { ConciergeAppointmentQuestionFlow } from '~types/concierge-enum'
import { useIdentityStore } from '~concierge/store/identityStore'

const APPOINTMENT_INITIAL_STATE = {
  flow: null,
  date: null,
  adviser: null,
  hour: null,
  vehicle: null
}

export const useAppointmentStore = defineStore({
  id: 'appointment',
  state: () => ({
    loadingDates: ref<boolean>(false),
    loadingVehicles: ref<boolean>(false),
    loadingHours: ref<boolean>(false),
    loadingAdvisers: ref<boolean>(false),
    availableVehicles: [] as Vehicle[],
    availableDates: new Map([]) as AvailabilityHoursByDateMap,
    disabledDates: [] as AppointmentDate[],
    availableAdvisers: [] as Adviser[],
    selectedDateHours: [] as Hour[],
    selectedDate: null as AppointmentDate | null,
    selectedVehicle: null as Vehicle | null,
    vehicleAvailabilityMessage: '' as string,
    queriedVehicleSlugs: {} as QueriedVehicleSlugs,
    hasMoreVehicles: false,
    selectedAdviser: null as Adviser | null,
    selectedHour: null as Hour | null,
    appointment: {
      flow: null,
      date: null,
      adviser: null,
      hour: null,
      vehicle: null,
      confirmed: false
    } as Appointment
  }),
  getters: {
    getAppointmentItem(state): AppointmentItem {
      if (state.appointment.flow === ConciergeAppointmentQuestionFlow.TestDriveAppointment) {
        const parts = []
        if (state.appointment.vehicle) {
          const identityStore = useIdentityStore()
          const showVehicleBrand = identityStore?.identity?.settings?.showVehicleBrand
          const { brand, model, trim, year } = state.appointment.vehicle
          if (brand && showVehicleBrand) parts.push(brand)
          if (model) parts.push(model)
          if (trim) parts.push(trim)
          if (year) parts.push(year)
        }

        return {
          photo: state.appointment.vehicle?.photo,
          name: parts.join(' ')
        }
      }
      return {
        photo: state.appointment.adviser?.photo,
        name: state.appointment.adviser?.first_name + ' ' + state.appointment.adviser?.last_name
      }
    },
    isAppointmentConfirmed(state): boolean {
      return this.isAppointmentSet && state.appointment.confirmed
    },
    isAppointmentSet(state): boolean {
      if (!state.appointment.flow) {
        return false
      }
      if (state.appointment.flow === ConciergeAppointmentQuestionFlow.TestDriveAppointment) {
        return (
          state.appointment.vehicle !== null &&
          Object.keys(state.appointment.vehicle).length > 0 &&
          state.appointment.hour !== null &&
          state.appointment.date !== null
        )
      }
      if (
        state.appointment.flow === ConciergeAppointmentQuestionFlow.AdviserSelectionAppointment ||
        state.appointment.flow === ConciergeAppointmentQuestionFlow.FreeAppointment
      ) {
        return (
          state.appointment.adviser !== null &&
          Object.keys(state.appointment.adviser).length > 0 &&
          state.appointment.hour !== null &&
          state.appointment.date !== null
        )
      } else {
        console.warn('Appointment not set')
      }
      return false
    },
    buildAppointmentForSubmission(state): Record<string, string | null> {
      return {
        date: state.appointment.date,
        time: state.appointment.hour,
        vehicle_id: state.appointment.vehicle?.id.toString() ?? '',
        adviser_id: state.appointment.adviser?.id.toString() ?? ''
      }
    }
  },
  actions: {
    confirmAppointment(): void {
      if (this.isAppointmentSet) {
        this.appointment.confirmed = true
      } else {
        console.error('Appointment not set')
      }
    },
    setAppointment(appointment: Appointment) {
      Object.assign(this.appointment, appointment)
    },
    resetAppointment() {
      Object.assign(this.appointment, APPOINTMENT_INITIAL_STATE)
    },
    resetAvailableDates() {
      this.availableDates.clear()
    },
    resetDisabledDates() {
      this.disabledDates = []
    },
    resetAvailableAdvisers() {
      this.availableAdvisers = []
    },
    resetAvailableVehicles() {
      this.availableVehicles = []
    },
    resetSelectedDateHours() {
      this.selectedDateHours = []
    },
    resetSelectedDate() {
      this.selectedDate = null
    },
    resetSelectedAdviser() {
      this.selectedAdviser = null
    },
    resetSelectedVehicle() {
      this.selectedVehicle = null
    },
    resetSelectedHour() {
      this.selectedHour = null
    },

    resetData() {
      this.resetAvailableDates()
      this.resetAvailableVehicles()
      this.resetDisabledDates()
      this.resetAvailableAdvisers()
      this.resetSelectedDateHours()
      this.resetSelectedDate()
      this.resetSelectedAdviser()
      this.resetSelectedVehicle()
      this.resetSelectedHour()
      this.resetAppointment()
    },
    resetDataExceptVehiclesList() {
      this.resetAvailableDates()
      this.resetDisabledDates()
      this.resetSelectedDateHours()
      this.resetSelectedDate()
      this.resetSelectedVehicle()
      this.resetSelectedHour()
      this.resetAppointment()
    },
    resetDataExceptAdvisersList() {
      this.resetAvailableDates()
      this.resetDisabledDates()
      this.resetSelectedDateHours()
      this.resetSelectedDate()
      this.resetSelectedAdviser()
      this.resetSelectedHour()
      this.resetAppointment()
    }
  }
})
